import { useState, useEffect } from 'react';
import { Globe, ExternalLink, Copy, Check, BookOpen, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

// Credentials come from GET /transcription-tool (editor/admin/staff only) —
// env-var backed on the server, never hardcoded here.
function CopyValue({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    // Guarded: clipboard API rejects in non-secure/embedded contexts — fall
    // back to a hidden textarea so copy still works and never throws.
    try { await navigator.clipboard.writeText(value); }
    catch {
      const ta = document.createElement('textarea');
      ta.value = value; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* no clipboard available */ }
      document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 w-14 shrink-0">{label}</span>
      <code data-testid={testId} className="text-[11px] text-zinc-100/90 bg-ug-surface/60 px-2 py-1 rounded border border-ug-border font-mono flex-1 truncate">{value}</code>
      <button onClick={copy} data-testid={`${testId}-copy`} title={`Copy ${label.toLowerCase()}`}
        className="tap-auto shrink-0 p-1 rounded text-slate-500 hover:text-oxidized-teal transition-colors">
        {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
}

const STEPS = [
  'Open the transcription tool (link below).',
  'Log in with the shared team account.',
  'Upload the footage audio for this project.',
  'Download the transcript and use it for subtitles and dialogue editing.',
];

export function TranscriptionPanel({ language }) {
  const { user } = useAuth();
  const [tool, setTool] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get('/transcription-tool').then(r => setTool(r.data || {})).catch(() => setTool({}));
  }, []);

  const configured = tool && (tool.url || tool.email || tool.password);

  return (
    <div data-testid="transcription-panel" className="rounded-md border border-oxidized-teal/30 bg-oxidized-teal/[0.05]">
      <button onClick={() => setOpen(!open)} data-testid="transcription-panel-toggle" aria-expanded={open}
        className="tap-auto w-full flex items-center gap-2 p-3 text-left hover:bg-oxidized-teal/[0.06] transition-colors">
        <Globe className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
        <span data-testid="transcription-panel-header" className="font-sans text-[10px] font-semibold uppercase tracking-wider text-oxidized-teal flex-1">
          {language} project — transcription required
        </span>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-oxidized-teal/60 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-oxidized-teal/60 shrink-0" />}
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid="transcription-panel-body">
          <ol className="space-y-1 mb-3 list-none">
            {STEPS.map((s, i) => (
              <li key={i} className="flex gap-2 text-[11px] text-zinc-100/80 leading-relaxed">
                <span className="font-mono text-[10px] text-oxidized-teal/70 shrink-0">{i + 1}.</span>{s}
              </li>
            ))}
          </ol>
          {tool === null ? null : configured ? (
            <div className="space-y-2">
              {tool.url && (
                <div className="pb-1.5">
                  <a href={tool.url} target="_blank" rel="noopener noreferrer" data-testid="transcription-tool-link"
                    className="tap-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-oxidized-teal/10 border border-oxidized-teal/25 hover:bg-oxidized-teal/20 transition-colors font-sans text-[10px] font-semibold uppercase tracking-wider text-oxidized-teal">
                    Open Transcription Tool <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {tool.email && <CopyValue label="Email" value={tool.email} testId="transcription-email" />}
              {tool.password && <CopyValue label="Password" value={tool.password} testId="transcription-password" />}
            </div>
          ) : (
            <p data-testid="transcription-not-configured" className="text-[10px] text-slate-500 italic">Tool access isn't configured yet — ask your PM for the transcription login.</p>
          )}
          {/* Editor-only: /editor/guide is role-gated, so admin/staff get no dead link. */}
          {user?.role === 'editor' && (
            <a href="/editor/guide#transcription" target="_blank" rel="noopener noreferrer" data-testid="transcription-manual-link"
              className="tap-auto mt-3 inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold text-oxidized-teal/70 hover:text-oxidized-teal transition-colors">
              <BookOpen className="w-3.5 h-3.5" /> Full instructions in the Editor Manual
            </a>
          )}
        </div>
      )}
    </div>
  );
}
